import {
  FormAnswer,
  Lead,
  LeadNote,
  LeadOrigin,
  LeadStatusHistory,
  NoteColor,
} from '../types'
import { supabase } from './supabaseClient'
import { encodeNote } from './mappers'
import { getDb, refresh } from './store'
import {
  describeError,
  isUuid,
  logFailure,
  logRequest,
  logSuccess,
} from '../utils/diagnostics'

/** Días que un elemento permanece en la papelera antes de borrarse. */
export const TRASH_RETENTION_DAYS = 30

export function listLeads(organizationId: string): Lead[] {
  return getDb()
    .leads.filter((l) => l.organizationId === organizationId && !l.deletedAt)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/** Leads en la papelera de la organización. */
export function listTrashedLeads(organizationId: string): Lead[] {
  return getDb()
    .leads.filter((l) => l.organizationId === organizationId && l.deletedAt)
    .sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''))
}

/** Días que faltan para el borrado definitivo de un elemento de la papelera. */
export function daysUntilPurge(deletedAt?: string): number {
  if (!deletedAt) return TRASH_RETENTION_DAYS
  const elapsed = (Date.now() - new Date(deletedAt).getTime()) / 86_400_000
  return Math.max(0, Math.ceil(TRASH_RETENTION_DAYS - elapsed))
}

export function getLead(organizationId: string, leadId: string): Lead | undefined {
  return getDb().leads.find(
    (l) => l.id === leadId && l.organizationId === organizationId,
  )
}

export function listNotes(leadId: string): LeadNote[] {
  return getDb()
    .notes.filter((n) => n.leadId === leadId && !n.deletedAt)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function listHistory(leadId: string): LeadStatusHistory[] {
  return getDb()
    .history.filter((h) => h.leadId === leadId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/** Registra una entrada en `lead_activities`. */
async function addHistory(
  entry: Omit<LeadStatusHistory, 'id' | 'createdAt'>,
): Promise<void> {
  const { data } = await supabase.auth.getUser()
  await supabase.from('lead_activities').insert({
    organization_id: entry.organizationId,
    lead_id: entry.leadId,
    type: entry.type,
    description: entry.description,
    created_by: data.user?.id ?? null,
  })
}

export async function updateLeadStage(
  leadId: string,
  stageId: string,
  authorName: string,
): Promise<void> {
  const db = getDb()
  const lead = db.leads.find((l) => l.id === leadId)
  if (!lead) return
  const stage = db.stages.find((s) => s.id === stageId)

  const { error } = await supabase
    .from('leads')
    .update({ stage_id: stageId })
    .eq('id', leadId)
  if (error) throw new Error(error.message)

  const { data } = await supabase.auth.getUser()
  await supabase.from('lead_status_history').insert({
    organization_id: lead.organizationId,
    lead_id: leadId,
    from_stage_id: lead.stageId || null,
    to_stage_id: stageId,
    changed_by: data.user?.id ?? null,
  })
  await addHistory({
    organizationId: lead.organizationId,
    leadId,
    type: 'estado',
    description: `Estado cambiado a ${stage?.name ?? stageId}.`,
    authorName,
  })
  await refresh()
}

export async function bulkUpdateStage(
  leadIds: string[],
  stageId: string,
  authorName: string,
): Promise<void> {
  for (const id of leadIds) await updateLeadStage(id, stageId, authorName)
}

export async function assignLead(
  leadId: string,
  userId: string | undefined,
  memberName: string,
  authorName: string,
): Promise<void> {
  const lead = getDb().leads.find((l) => l.id === leadId)
  if (!lead) return
  const { error } = await supabase
    .from('leads')
    .update({ assigned_to: userId ?? null })
    .eq('id', leadId)
  if (error) throw new Error(error.message)
  await addHistory({
    organizationId: lead.organizationId,
    leadId,
    type: 'asignacion',
    description: userId ? `Asignado a ${memberName}.` : 'Asignación retirada.',
    authorName,
  })
  await refresh()
}

/**
 * Borrado lógico: el lead sale de Leads y del embudo, pero conserva sus
 * notas, respuestas e historial dentro de la papelera.
 */
export async function deleteLead(
  leadId: string,
  deletedBy = 'Sistema',
): Promise<void> {
  const lead = getDb().leads.find((l) => l.id === leadId)
  if (!isUuid(leadId)) {
    throw new Error(
      'El identificador del lead no es válido, así que no se puede mover a la papelera.',
    )
  }
  const { data } = await supabase.auth.getUser()
  const payload = {
    deleted_at: new Date().toISOString(),
    deleted_by: data.user?.id ?? null,
  }
  logRequest('leads.moverAPapelera', { leadId, ...payload })
  const { data: updated, error } = await supabase
    .from('leads')
    .update(payload)
    .eq('id', leadId)
    .select('id')
  if (error) {
    logFailure('leads.moverAPapelera', error, { leadId, ...payload })
    throw new Error(describeError(error))
  }
  // Sin error pero sin filas devueltas: RLS ha filtrado la fila.
  if (!updated || updated.length === 0) {
    logFailure(
      'leads.moverAPapelera',
      { message: 'La actualización no ha afectado a ninguna fila.' },
      { leadId },
    )
    throw new Error(
      'No se ha podido mover el lead a la papelera: no se ha encontrado o no tienes permiso sobre él.',
    )
  }
  logSuccess('leads.moverAPapelera', updated)
  if (lead) {
    await addHistory({
      organizationId: lead.organizationId,
      leadId,
      type: 'estado',
      description: 'Lead movido a la papelera.',
      authorName: deletedBy,
    })
  }
  await refresh()
}

export async function bulkDeleteLeads(
  leadIds: string[],
  deletedBy: string,
): Promise<void> {
  for (const id of leadIds) await deleteLead(id, deletedBy)
}

/** Devuelve un lead de la papelera a la lista activa. */
export async function restoreLead(
  leadId: string,
  authorName = 'Sistema',
): Promise<void> {
  const lead = getDb().leads.find((l) => l.id === leadId)
  const { error } = await supabase
    .from('leads')
    .update({ deleted_at: null, deleted_by: null })
    .eq('id', leadId)
  if (error) throw new Error(error.message)
  if (lead) {
    await addHistory({
      organizationId: lead.organizationId,
      leadId,
      type: 'estado',
      description: 'Lead restaurado desde la papelera.',
      authorName,
    })
  }
  await refresh()
}

/** Elimina el lead y toda su información de forma irreversible. */
export async function purgeLead(leadId: string): Promise<void> {
  const { error } = await supabase.from('leads').delete().eq('id', leadId)
  if (error) throw new Error(error.message)
  await refresh()
}

/** Vacía por completo la papelera de una organización. */
export async function emptyTrash(organizationId: string): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .delete()
    .eq('organization_id', organizationId)
    .not('deleted_at', 'is', null)
  if (error) throw new Error(error.message)
  await refresh()
}

export async function addNote(
  organizationId: string,
  leadId: string,
  authorId: string,
  authorName: string,
  body: string,
  title?: string,
  color: NoteColor = 'gray',
): Promise<void> {
  const { error } = await supabase.from('lead_notes').insert({
    organization_id: organizationId,
    lead_id: leadId,
    author_id: authorId,
    content: encodeNote(body, title),
    color,
  })
  if (error) throw new Error(error.message)
  await addHistory({
    organizationId,
    leadId,
    type: 'nota',
    description: title ? `Nota añadida: ${title}` : 'Nota añadida.',
    authorName,
  })
  await refresh()
}

export async function updateNote(
  noteId: string,
  body: string,
  title?: string,
  color?: NoteColor,
): Promise<void> {
  const patch: Record<string, unknown> = {
    content: encodeNote(body, title),
  }
  if (color) patch.color = color
  const { error } = await supabase.from('lead_notes').update(patch).eq('id', noteId)
  if (error) throw new Error(error.message)
  await refresh()
}

/** Cambia solo el color de una nota existente. */
export async function updateNoteColor(
  noteId: string,
  color: NoteColor,
): Promise<void> {
  const { error } = await supabase
    .from('lead_notes')
    .update({ color })
    .eq('id', noteId)
  if (error) throw new Error(error.message)
  await refresh()
}

/** Borrado lógico de la nota: deja de mostrarse pero se conserva. */
export async function deleteNote(noteId: string): Promise<void> {
  const { error } = await supabase
    .from('lead_notes')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', noteId)
  if (error) throw new Error(error.message)
  await refresh()
}

export interface SubmitLeadPayload {
  organizationId: string
  formId: string
  landingId?: string
  contact: Record<string, string>
  answers: FormAnswer[]
  origin: LeadOrigin
}

export interface SubmitLeadResult {
  leadId: string
  submissionId: string
}

/**
 * ÚNICO punto de entrada de un lead público.
 *
 * Llama a la función `submit_public_form`, la única forma permitida de crear
 * un lead desde una landing o un formulario público con un visitante
 * anónimo. Nunca se insertan leads directamente sin sesión.
 */
export async function submitLead(
  payload: SubmitLeadPayload,
): Promise<SubmitLeadResult> {
  const answers = payload.answers.map((answer) => ({
    question_id: answer.questionId,
    value: Array.isArray(answer.value) ? answer.value.join(', ') : answer.value,
  }))

  // Validación previa: un id ausente o no-UUID provoca un fallo silencioso
  // en la función RPC, así que se detecta aquí con un mensaje explícito.
  if (!isUuid(payload.formId)) {
    logFailure(
      'submit_public_form',
      { message: `form_id inválido o ausente: "${payload.formId}"` },
      payload,
    )
    throw new Error(
      `El formulario no está guardado en la base de datos (form_id inválido: "${payload.formId}"). Abre el formulario en el panel y pulsa Guardar.`,
    )
  }
  if (payload.landingId && !isUuid(payload.landingId)) {
    logFailure(
      'submit_public_form',
      { message: `landing_id inválido: "${payload.landingId}"` },
      payload,
    )
    throw new Error(
      `La landing no está guardada en la base de datos (landing_id inválido: "${payload.landingId}").`,
    )
  }
  const invalidAnswer = answers.find((a) => !isUuid(a.question_id))
  if (invalidAnswer) {
    logFailure(
      'submit_public_form',
      {
        message: `question_id inválido: "${invalidAnswer.question_id}". Las preguntas no proceden de la base de datos.`,
      },
      payload,
    )
    throw new Error(
      `Una de las preguntas no está guardada en la base de datos (question_id "${invalidAnswer.question_id}"). Abre el formulario en el panel y pulsa Guardar.`,
    )
  }

  // `p_answers` solo admite objetos { question_id, value }. Los datos de
  // contacto no son preguntas, así que viajan en `p_utm.contact`, desde donde
  // la función los vuelca en las columnas del lead.
  const rpcArgs = {
    p_form_id: payload.formId,
    p_landing_id: payload.landingId ?? null,
    p_answers: answers,
    p_utm: {
      utm_source: payload.origin.source,
      utm_medium: payload.origin.medium,
      utm_campaign: payload.origin.campaign,
      utm_content: payload.origin.content,
      utm_term: payload.origin.term,
      referrer: payload.origin.referrer,
      contact: payload.contact,
      full_name: payload.contact.fullName ?? payload.contact.firstName ?? '',
      company: payload.contact.company ?? '',
      phone: payload.contact.phone ?? '',
      email: payload.contact.email ?? '',
    },
  }

  logRequest('submit_public_form', rpcArgs)

  const { data, error } = await supabase.rpc('submit_public_form', rpcArgs)

  if (error) {
    logFailure('submit_public_form', error, rpcArgs)
    throw new Error(`No hemos podido enviar tu solicitud: ${describeError(error)}`)
  }

  logSuccess('submit_public_form', data)

  const submissionId = data as string
  return { leadId: submissionId, submissionId }
}
